/** @jsxImportSource @emotion/react */
import Input from 'components/atoms/Input'
import {
    AdjustmentCode,
    DeniedBadge,
    isDeniedAdjudication,
    RemarkCodeList,
} from 'components/lab/billingEraReports/AdjustmentCodes'
import React from 'react'
import { Button } from 'react-bootstrap'
import { downloadEobPdf } from 'helpers/eob'
import { ChargeAccountTransaction, ServiceLineAdjustmentCode } from 'store/types'
import { NotesIndicator } from 'components/lab/billingNotes/BillingNotes'
import { AttachmentsIndicator, PaymentAttachmentsModal } from './ChargeAttachmentsModal'
import { serviceLineGrid } from './ServiceLineRow'
import { WriteOffReasonField } from './WriteOffReasonField'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import { formatEasternDate } from 'helpers/dates'
import { PATIENT_WRITE_OFF_REASONS } from 'ts-common/utils/writeOffReasons.utils'
import {
  changedPaymentDetailsBody,
  PaymentDetailsDraft,
  paymentDetailsDraftOf,
  paymentDetailsSummary,
} from './PaymentMethodDetailsFields'
import { PaymentDetailsModal } from './PaymentDetailsModal'

interface Props {
  transaction: ChargeAccountTransaction
  patientId: number
  isEditing: boolean
  isSaving: boolean
  isReversing: boolean
  /** Earlier reversals already back this row out in full — nothing left to reverse. */
  isFullyReversed?: boolean
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  onReverse: () => void
  onFieldChange: (field: keyof ChargeAccountTransaction, value: number | string) => void
  onOpenNotes: () => void
  /** Set once a save was attempted with a write-off but no reason. */
  writeOffReasonError?: boolean
  /** The row was posted as "Other" already, so its explanation is on record and is not asked for again. */
  writeOffNoteOnRecord?: boolean
  /** Fired after a payment document is added or removed, so counts refresh. */
  onAttachmentsChange?: () => void
  /** The payment's details while this row is being edited, when they can be corrected. */
  paymentDetails?: PaymentDetailsDraft | null
  onPaymentDetailsChange?: (values: PaymentDetailsDraft) => void
}

// Older hand-posted and imported payments carry these words where the Claim
// Identifier belongs; they name how the payment was entered, not a claim.
const NOT_A_CLAIM_IDENTIFIER = ['MANUAL', 'IMPORTED']

const formatDate = (date: string | Date | null | undefined) => formatEasternDate(date, '--')

/**
 * A row an ERA claim posted. It is reversed only together with the rest of its
 * payment, which is also what returns the claim to the ERA pages — the server
 * refuses it on its own. Patient write-offs entered later are not the posting.
 */
export const reversesWholePayment = (transaction: ChargeAccountTransaction): boolean =>
  (!transaction.type || transaction.type === 'SYSTEM_PAYMENT')
  && !transaction.reversesId
  && Boolean(transaction.eraClaimId)

export const TransactionRow: React.FC<Props> = ({
  transaction,
  patientId,
  isEditing,
  isSaving,
  isReversing,
  isFullyReversed = false,
  onEdit,
  onSave,
  onCancel,
  onReverse,
  onFieldChange,
  onOpenNotes,
  writeOffReasonError,
  writeOffNoteOnRecord,
  onAttachmentsChange,
  paymentDetails,
  onPaymentDetailsChange,
}) => {
  const [eobLoading, setEobLoading] = React.useState(false)
  const [attachmentsOpen, setAttachmentsOpen] = React.useState(false)
  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false)
  const notes = transaction.notes ?? []
  // A transaction replayed from history without an id has no stable key for a
  // note to point at — and, on this merged view, no way to be addressed for an
  // edit or a reversal either.
  const canTakeNotes = Boolean(transaction.id)
  const isReversal = transaction.type === 'REVERSAL'
  // A biller-forgiven patient balance: its only money is `patientWriteOff`.
  // Never edited (reverse and re-enter instead), reversible under its own
  // permission — forgiving debt is a different authority than posting money.
  const isPatientWriteOff = transaction.type === 'PATIENT_WRITE_OFF'
  const claimIdentifier = transaction.txId && !NOT_A_CLAIM_IDENTIFIER.includes(String(transaction.txId).trim().toUpperCase())
    ? transaction.txId
    : null
  // A reversal of a patient write-off carries the money back the same way.
  const patientWriteOffAmount = Number(transaction.patientWriteOff ?? 0) || 0
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const canWriteOffPatientBalance = useLabPermission(UserPermission.WRITE_OFF_PATIENT_BALANCE)
  const isLocked =
    transaction.type === 'APPLIED_PAYMENT' || isPatientWriteOff || !transaction.id || !canPostInsurancePayments
  // Applied-payment settlements are returned via the application refund flow,
  // not reversed — the backend refuses them too.
  const canReverse = !isReversal && !isFullyReversed && Boolean(transaction.id) && (
    isPatientWriteOff
      ? canWriteOffPatientBalance
      : transaction.type !== 'APPLIED_PAYMENT' && canPostInsurancePayments
  )
  const formatInitial = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return ''
    if (typeof value === 'number') return value.toFixed(2)
    return String(value)
  }

  const detailsSummary = transaction.paymentDetails ? paymentDetailsSummary(transaction.paymentDetails) : []
  const postedBy = transaction.postedByName
    ? `Posted by ${transaction.postedByName}`
    : transaction.postedAutomatically ? 'Posted automatically from ERA' : null

  const editsDetails = isEditing && !isLocked && Boolean(paymentDetails && onPaymentDetailsChange)
  const detailsChanged = Boolean(paymentDetails && transaction.paymentDetails
    && changedPaymentDetailsBody(paymentDetails, paymentDetailsDraftOf(transaction.paymentDetails)))

  React.useEffect(() => {
    setDetailsModalOpen(editsDetails)
  }, [editsDetails])

  const handleTextChange = (field: keyof ChargeAccountTransaction) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(field, e.target.value)
  }

  const moneyInput = (field: 'allowed' | 'paidRefund' | 'transfers' | 'copay' | 'writeOff') => (
    <Input
      type="text"
      className="form-control form-control-sm"
      value={formatInitial(transaction[field])}
      disabled={!isEditing || isLocked}
      handleChange={handleTextChange(field)}
      css={{
        backgroundColor: isEditing ? 'white' : 'transparent',
        border: isEditing ? '1px solid #ced4da' : '1px solid transparent',
        cursor: isEditing ? 'text' : 'default',
      }}
    />
  )

  return (
    <div
      css={{
        ...serviceLineGrid,
        padding: '0.75rem 0',
        backgroundColor: '#FFFDF6',
        borderBottom: '1px solid #bbbbbb',
      }}>
      <div />
      {/* Info block sits under CPT…Exp. Allowed; the five inputs line up with
          the running Allowed…Write-off columns above them. */}
      <div css={{ gridColumn: 'span 5', fontSize: '0.875rem' }}>
        <div>
          <strong>Tr:</strong> {formatDate(transaction.date)}{' '}
          {!isPatientWriteOff && claimIdentifier && (
            <span css={{ color: '#6c757d' }} title="Claim Identifier">#{claimIdentifier}</span>
          )}
          {isPatientWriteOff && (
            <span
              css={{
                marginLeft: '0.5rem',
                backgroundColor: '#6f42c1',
                color: 'white',
                borderRadius: '0.25rem',
                padding: '0.05rem 0.35rem',
                fontSize: '0.688rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}>
              Patient Write-off
            </span>
          )}
          {isReversal && (
            <span
              css={{
                marginLeft: '0.5rem',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '0.25rem',
                padding: '0.05rem 0.35rem',
                fontSize: '0.688rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}>
              Rev
            </span>
          )}
        </div>
        <div css={{ color: '#6c757d' }}>
          {transaction.paymentBy}
          {isPatientWriteOff ? (
            <span css={{ marginLeft: 6, fontSize: '0.8rem' }}>· forgave patient responsibility — no money moved</span>
          ) : transaction.sourceLabel ? (
            <span css={{ marginLeft: 6, fontSize: '0.8rem' }}>· {transaction.sourceLabel}</span>
          ) : null}
          {!isPatientWriteOff && detailsSummary.map((part) => (
            <span key={part} css={{ marginLeft: 6, fontSize: '0.8rem' }}>· {part}</span>
          ))}
        </div>
        {postedBy && (
          <div css={{ color: '#6c757d', fontSize: '0.8rem' }}>{postedBy}</div>
        )}
        {((transaction.adjustments?.length ?? 0) > 0 ||
          (transaction.remarkCodes?.length ?? 0) > 0) && (
          <div css={{ fontSize: '0.813rem', marginTop: '0.25rem' }}>
            {isDeniedAdjudication(transaction.paidRefund, transaction.adjustments) && (
              <span css={{ marginRight: 6 }}>
                <DeniedBadge />
              </span>
            )}
            {(transaction.adjustments || []).map((a: ServiceLineAdjustmentCode, i: number) => (
              <span key={i} css={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                <AdjustmentCode group={a.group} reason={a.reason} />
                {a.amount !== undefined && a.amount !== null && (
                  <span css={{ color: '#6c757d' }}> ${Number(a.amount).toFixed(2)}</span>
                )}
              </span>
            ))}
            {(transaction.remarkCodes?.length ?? 0) > 0 && (
              <span css={{ color: '#6c757d' }}>
                Remarks: <RemarkCodeList codes={transaction.remarkCodes} />
              </span>
            )}
          </div>
        )}
      </div>
      {isPatientWriteOff || (isReversal && patientWriteOffAmount !== 0) ? (
        <>
          {/* Patient write-offs (and their reversals) carry no payer money —
              the amount lands in the Patient Resp. column with its sign: a
              forgiveness reduces it, a reversal restores it. */}
          <div />
          <div />
          <div />
          <div css={{ fontWeight: 600, color: '#6f42c1', whiteSpace: 'nowrap' }}>
            {patientWriteOffAmount > 0 ? '-' : '+'}${Math.abs(patientWriteOffAmount).toFixed(2)}
          </div>
          <div>
            <WriteOffReasonField
              writeOff={Math.abs(patientWriteOffAmount)}
              reason={transaction.writeOffReason}
              reasons={PATIENT_WRITE_OFF_REASONS}
              readOnly
            />
          </div>
        </>
      ) : (
        <>
          <div>{moneyInput('allowed')}</div>
          <div>{moneyInput('paidRefund')}</div>
          <div>{moneyInput('transfers')}</div>
          <div>{moneyInput('copay')}</div>
          <div css={{ position: 'relative' }}>
            {moneyInput('writeOff')}
            {/* ERA-posted rows carry the reason their payer CARC codes (the
                badges above) stand for, so every write-off shows one here.
                Read-only, it hangs under the input so the input stays level
                with the rest of the row. */}
            <div css={!isEditing || isLocked ? { position: 'absolute', top: '100%', left: 0, right: 0 } : undefined}>
              <WriteOffReasonField
                writeOff={transaction.writeOff}
                reason={transaction.writeOffReason}
                note={String((transaction as Record<string, unknown>).writeOffNote ?? '')}
                readOnly={!isEditing || isLocked}
                disabled={isLocked}
                hasError={writeOffReasonError}
                noteOnRecord={writeOffNoteOnRecord}
                onReasonChange={(value) => onFieldChange('writeOffReason' as keyof ChargeAccountTransaction, value)}
                onNoteChange={(value) => onFieldChange('writeOffNote' as keyof ChargeAccountTransaction, value)}
              />
            </div>
          </div>
        </>
      )}
      <div
        className="d-flex justify-content-end align-items-center"
        css={{ gridColumn: 'span 3', flexWrap: 'nowrap', gap: '0.5rem', paddingRight: '0.5rem' }}>
        {/* Stacked, so a row carrying every action still fits its column. */}
        <div className="d-flex flex-column align-items-stretch" css={{ gap: '0.25rem' }}>
          {canTakeNotes && (
            <NotesIndicator count={notes.length} isOpen={false} onToggle={onOpenNotes} />
          )}
          {/* Documents on the payment behind this row — in practice the paper EOB
              a hand-entered adjudication needs before its COB claim can go out.
              On the posting entry only, like the EOB button: a reversal is a
              contra-entry of the same payment, not a second one to file against. */}
          {!isReversal && !isPatientWriteOff && transaction.paymentId && (
            <AttachmentsIndicator
              count={transaction.attachmentsCount ?? 0}
              isOpen={attachmentsOpen}
              onToggle={() => setAttachmentsOpen(true)}
            />
          )}
        </div>
        {/* The EOB of the payment behind this entry — on the posting entry,
            not on reversals, so a payment appears once per line it touched. */}
        {!isReversal && !isPatientWriteOff && transaction.eraClaimId && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            title="Print the EOB for this payment"
            disabled={eobLoading}
            css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}
            onClick={async (e) => {
              e.stopPropagation()
              setEobLoading(true)
              await downloadEobPdf(
                `/lab/patients/${patientId}/account/payments/${transaction.paymentId}/eob`,
                `EOB_Payment_${transaction.paymentId}.pdf`,
              )
              setEobLoading(false)
            }}>
            {eobLoading ? 'Preparing …' : 'EOB of Payment'}
          </button>
        )}
        {!isLocked && isEditing ? (
          <>
            {editsDetails && (
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={isSaving}
                onClick={(e) => {
                  e.stopPropagation()
                  setDetailsModalOpen(true)
                }}
                css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
                Payment details{detailsChanged ? ' •' : ''}
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              disabled={isSaving}
              onClick={(e) => {
                e.stopPropagation()
                onSave()
              }}
              css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isSaving}
              onClick={(e) => {
                e.stopPropagation()
                onCancel()
              }}
              css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            {canReverse && (
              <Button
                variant="outline-danger"
                size="sm"
                disabled={isReversing}
                title={reversesWholePayment(transaction)
                  ? 'Reverses every service line of this payment and releases its ERA claim'
                  : undefined}
                onClick={(e) => {
                  e.stopPropagation()
                  onReverse()
                }}
                css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
                {isReversing ? 'Reversing...' : reversesWholePayment(transaction) ? 'Reverse Payment' : 'Reverse Line'}
              </Button>
            )}
            {!isLocked && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
                css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
                Edit
              </Button>
            )}
          </>
        )}
      </div>

      {detailsModalOpen && paymentDetails && onPaymentDetailsChange && (
        <PaymentDetailsModal
          values={paymentDetails}
          onClose={() => setDetailsModalOpen(false)}
          onDone={(values) => {
            onPaymentDetailsChange(values)
            setDetailsModalOpen(false)
          }}
        />
      )}

      <PaymentAttachmentsModal
        paymentId={attachmentsOpen ? transaction.paymentId : null}
        sourceLabel={transaction.sourceLabel}
        onClose={() => setAttachmentsOpen(false)}
        onChange={onAttachmentsChange}
      />
    </div>
  )
}
